import { AlertCircle, RefreshCw, Clock, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorMessageProps {
  /** Error type for different styling and icons */
  type?: "default" | "conflict" | "rate-limit" | "network" | "payment" | "session";
  /** Error title (optional) */
  title?: string;
  /** Error message to display */
  message: string;
  /** Callback for retry button (if not provided, no retry button shown) */
  onRetry?: () => void;
  /** Custom retry button text */
  retryText?: string;
  /** For rate-limit errors: seconds until retry allowed */
  retryAfter?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable error message component with consistent styling.
 * Supports different error types with appropriate icons and colors.
 */
export function ErrorMessage({
  type = "default",
  title,
  message,
  onRetry,
  retryText = "Try Again",
  retryAfter,
  className,
}: ErrorMessageProps) {
  const configs = {
    default: {
      icon: <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-destructive/10",
      textClass: "text-destructive",
      borderClass: "border-destructive/20",
    },
    conflict: {
      icon: <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-orange-50",
      textClass: "text-orange-700",
      borderClass: "border-orange-200",
    },
    "rate-limit": {
      icon: <Clock className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-yellow-50",
      textClass: "text-yellow-700",
      borderClass: "border-yellow-200",
    },
    network: {
      icon: <WifiOff className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-gray-100",
      textClass: "text-gray-700",
      borderClass: "border-gray-200",
    },
    payment: {
      icon: <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-destructive/10",
      textClass: "text-destructive",
      borderClass: "border-destructive/20",
    },
    session: {
      icon: <Clock className="h-5 w-5 flex-shrink-0" aria-hidden="true" />,
      bgClass: "bg-orange-50",
      textClass: "text-orange-700",
      borderClass: "border-orange-200",
    },
  };

  const config = configs[type];

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "flex flex-col gap-3 p-4 rounded-lg border",
        config.bgClass,
        config.borderClass,
        className
      )}
    >
      <div className="flex items-start gap-3">
        <span className={config.textClass}>{config.icon}</span>
        <div className="flex-1 min-w-0">
          {title && (
            <p className={cn("font-semibold text-sm", config.textClass)}>
              {title}
            </p>
          )}
          <p className={cn("text-sm", config.textClass)}>{message}</p>
          {type === "rate-limit" && retryAfter !== undefined && retryAfter > 0 && (
            <p className="text-xs text-yellow-600 mt-1">
              Please wait {retryAfter} seconds before trying again.
            </p>
          )}
        </div>
      </div>

      {onRetry && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={type === "rate-limit" && retryAfter !== undefined && retryAfter > 0}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {retryText}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ErrorMessage;
